import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ShieldCheck, ArrowLeft } from "lucide-react";
import api, { getErr } from "../lib/api";
import { NeonButton, NeonCard } from "../components/Neon";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [email, setEmail] = useState(params.get("email") || "");
  const [code, setCode] = useState(params.get("code") || "");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email) { toast.error("Please enter your account email."); return; }
    if (!/^\d{6}$/.test(code.trim())) { toast.error("Enter the 6-digit code from your email."); return; }
    if (password.length < 10) { toast.error("Password must be at least 10 characters."); return; }
    if (password !== confirm) { toast.error("Passwords do not match."); return; }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { email, code: code.trim(), new_password: password });
      toast.success("Password updated — please log in with your new password.");
      navigate("/login");
    } catch (err) {
      toast.error(getErr(err, "Could not reset password. The code may be invalid or expired."));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mx-auto max-w-md px-4 py-16">
      <NeonCard variant="flow" innerClassName="p-8">
        <Link to="/login" className="flex items-center gap-2 text-sm text-[var(--text-muted)] hover:text-white" data-testid="reset-back-login"><ArrowLeft className="h-4 w-4" /> Back to login</Link>
        <h1 className="mt-4 font-display uppercase text-4xl text-white glow-white">Set New Password</h1>
        <p className="mt-1 text-sm text-[var(--text-muted)]">Enter the 6-digit code we emailed you, then choose a new password.</p>
        <form onSubmit={submit} className="mt-6 space-y-4">
          <div>
            <label className="text-xs uppercase tracking-widest text-[var(--text-muted)]">Email</label>
            <input data-testid="reset-email" type="email" autoComplete="username" spellCheck="false" required value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1 w-full bg-[var(--surface-2)] border border-white/10 rounded-xl px-3 py-2.5 text-white focus:outline-none focus:border-[var(--neon-cyan)]" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-[var(--text-muted)]">6-digit code</label>
            <input data-testid="reset-code" inputMode="numeric" maxLength={6} required value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} placeholder="123456" className="mt-1 w-full bg-[var(--surface-2)] border border-white/10 rounded-xl px-3 py-2.5 text-white tracking-[0.4em] font-display text-lg focus:outline-none focus:border-[var(--neon-cyan)]" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-[var(--text-muted)]">New password</label>
            <input data-testid="reset-password" type="password" autoComplete="new-password" required value={password} onChange={(e) => setPassword(e.target.value)} className="mt-1 w-full bg-[var(--surface-2)] border border-white/10 rounded-xl px-3 py-2.5 text-white focus:outline-none focus:border-[var(--neon-cyan)]" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-[var(--text-muted)]">Confirm new password</label>
            <input data-testid="reset-confirm" type="password" autoComplete="new-password" required value={confirm} onChange={(e) => setConfirm(e.target.value)} className="mt-1 w-full bg-[var(--surface-2)] border border-white/10 rounded-xl px-3 py-2.5 text-white focus:outline-none focus:border-[var(--neon-cyan)]" />
          </div>
          <p className="flex items-center gap-1.5 text-[11px] text-[var(--text-muted)]"><ShieldCheck className="h-3.5 w-3.5" /> Minimum 10 characters with upper &amp; lower case and a number.</p>
          <NeonButton type="submit" variant="pink" className="w-full" data-testid="reset-submit" loading={loading}>Update Password</NeonButton>
        </form>
        <p className="mt-5 text-sm text-[var(--text-muted)]">Need a new code? <Link to="/forgot-password" className="text-[var(--neon-pink)] hover:underline" data-testid="reset-request-new">Request another</Link></p>
      </NeonCard>
    </div>
  );
}
